import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarPriceDependents } from "./solar-price-dependents"
import type { SolarSystemType } from "../dto/solar-proposal-product-offering.dto"
import type { SolarProposalSummaryDto } from "../dto/solar-proposal-summary.dto"
import { solarProposalService } from "../services/solar-proposal.service"

/**
 * Saves the Mount alone (the Summary, when Finance Partner & Equipment can't
 * be saved). The answer is the readiness read. PowurOS prices the mounting
 * type, so the price reads refetch too; nothing is recalculated.
 */
export default function useUpdateSolarProposalMount(): UseMutationResult<
  SolarProposalSummaryDto,
  unknown,
  { proposalId: string; systemType: SolarSystemType }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, systemType }) =>
      solarProposalService.putMount(proposalId, { systemType }),
    onSuccess: (summary, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.summary(proposalId),
        summary,
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.productOffering(proposalId),
        exact: true,
      })
      invalidateSolarPriceDependents(queryClient, proposalId, {
        except: [`savings`, `header`],
      })
    },
    // A lock may be why it failed: the step reads its state again.
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.productOffering(proposalId),
        exact: true,
      })
    },
  })
}
